import { useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { useGetAllDealerAgreementTypeQuery, useCreateDealerAgreementTypeMutation, useUpdateDealerAgreementTypeMutation, useDeleteDealerAgreementTypeMutation } from '../../../../../api/endpoints/dealerAgreementType';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { toastService } from '../../../../../utils/toastService';
import { RowState } from '../../../../../utils/dealerApprovalEnums';

// Sayfadaki diğer (etiketsiz) ConfirmDialog'larla çakışmaması için TabsView'daki ConfirmDialog ile eşleşen anahtar.
export const DEALER_AGREEMENT_TYPE_DELETE_CONFIRM_KEY = 'dealerAgreementTypeDelete';

export const useDealerAgreementTypeData = ({ dealerAddressBrandId, dealer, pageSize = 10, enabled = true }) => {
    const { th } = useLanguage();
    const [pageNumber, setPageNumber] = useState(1);

    const dealerId = dealer ? dealer.id : null;

    const { data = { items: [], count: 0 }, isLoading, isFetching, refetch } = useGetAllDealerAgreementTypeQuery({ dealerId, dealerAddressBrandId, pageNumber, pageSize }, { skip: !enabled });

    const [createAgreementType] = useCreateDealerAgreementTypeMutation();
    const [updateAgreementType] = useUpdateDealerAgreementTypeMutation();
    const [deleteAgreementType] = useDeleteDealerAgreementTypeMutation();

    const columns = [
        { field: 'type', header: th('customerAddressBrand.tabs.dealerAgreementType.columns.type'), body: (row) => getTypeLabel(row.type) },
        { field: 'agreementType', header: th('customerAddressBrand.tabs.dealerAgreementType.columns.agreementType'), body: (row) => getAgreementTypeLabel(row.agreementType) },
        { field: 'price', header: th('customerAddressBrand.tabs.dealerAgreementType.columns.price') },
        { field: 'curency', header: th('customerAddressBrand.tabs.dealerAgreementType.columns.currency') },
        {
            field: 'contractStartDate',
            header: th('customerAddressBrand.tabs.dealerAgreementType.columns.contractStartDate'),
            body: (row) => formatDateOnly(row.contractStartDate)
        },
        {
            field: 'contractEndDate',
            header: th('customerAddressBrand.tabs.dealerAgreementType.columns.contractEndDate'),
            body: (row) => formatDateOnly(row.contractEndDate)
        }
    ];

    const save = async (formData) => {
        try {
            const payload = {
                ...formData,
                dealerAddressBrandId,
                price: Number(formData.price),
                contractStartDate: formData.contractStartDate ? new Date(formData.contractStartDate).toISOString() : null,
                contractEndDate: formData.contractEndDate ? new Date(formData.contractEndDate).toISOString() : null
            };

            if (formData.id) {
                await updateAgreementType(payload).unwrap();
            } else {
                await createAgreementType(payload).unwrap();
            }
            refetch();
        } catch (error) {
            console.error('❌ Agreement type save error:', error);
        }
    };

    const remove = async (id, successKey = 'customerAddressBrand.tabs.dealerAgreementType.delete.success') => {
        try {
            await deleteAgreementType({ id, dealerAddressBrandId }).unwrap();
            toastService.success(th(successKey));
            refetch();
        } catch {
            // Hata mesajı (404 / 409 kilit / bekleyen değişiklik) global baseQueryWithReauth tarafından gösteriliyor.
        }
    };

    // Canlı (rowState=0) satır silme talebine alınır (onay sonrası geçerli); taslaktaki eklenecek (rowState=1)
    // satırda ise ekleme doğrudan geri alınır. Diğer durumlarda silme yapılmaz.
    const confirmRemove = (row) => {
        const rowState = row?.rowState ?? RowState.LIVE;
        if (!row?.id || (rowState !== RowState.LIVE && rowState !== RowState.PENDING_INSERT)) return;
        const prefix = rowState === RowState.PENDING_INSERT ? 'customerAddressBrand.tabs.dealerAgreementType.withdrawInsert' : 'customerAddressBrand.tabs.dealerAgreementType.delete';
        confirmDialog({
            tagKey: DEALER_AGREEMENT_TYPE_DELETE_CONFIRM_KEY,
            header: th(`${prefix}.header`),
            message: th(`${prefix}.message`),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th(`${prefix}.accept`),
            rejectLabel: th(`${prefix}.reject`),
            acceptClassName: 'p-button-danger',
            accept: () => remove(row.id, `${prefix}.success`)
        });
    };

    return {
        items: data.items,
        count: data.count,
        isLoading: isLoading || isFetching,
        pageNumber,
        setPageNumber,
        pageSize,
        save,
        remove,
        confirmRemove,
        refetch,
        columns
    };
};

const getTypeLabel = (value) => {
    switch (value) {
        case 1:
            return 'Yol Yardımı';
        default:
            return '-';
    }
};

const getAgreementTypeLabel = (value) => {
    switch (value) {
        case 1:
            return 'KM';
        case 2:
            return 'Saat';
        default:
            return '-';
    }
};
